/**
 * Footnote support for the Tiptap markdown editor.
 *
 * Markdown form (Pandoc / GFM):
 *
 *   Body text with a note.[^1]
 *
 *   [^1]: The note itself, which may continue
 *       on 4-space-indented lines.
 *
 * Without this extension the syntax isn't just unrendered — it's *corrupted*
 * on save. `@tiptap/markdown` (marked under the hood) has no footnote
 * tokenizer, so `[^1]` arrives as literal text and the serializer's
 * markdown-delimiter escaping writes it back as `\[^1\]`. Every open+save of a
 * document with footnotes rewrites the file.
 *
 * Strategy — two nodes, each owning one half of the syntax, mirroring how
 * `math.ts` ports a tokenizer to marked's contract:
 *
 *  - {@link FootnoteReference} — inline atom for `[^label]`. Renders as an
 *    empty `<sup data-footnote-ref>`; the *displayed* number comes from a
 *    decoration (see below), because a reference's number depends on its
 *    position in the document, not on its label.
 *  - {@link FootnoteDefinition} — block node for `[^label]: …`, holding
 *    ordinary block content so the note body is editable prose (lists, code,
 *    links, more than one paragraph). Definitions render **in place**, i.e.
 *    wherever the author wrote them, rather than being hoisted into a
 *    generated section — the editor shows the document, and the round-trip
 *    stays byte-stable.
 *
 * Numbering is a pure function of the document ({@link indexFootnotes}),
 * projected onto the DOM by a ProseMirror decoration plugin as
 * `data-footnote-number` / `data-footnote-missing` / `data-footnote-orphan`
 * — the same "extension paints data-attrs, consumer styles them" split
 * `wiki-link.ts` uses. Hearth's `index.css` renders the number via
 * `content: attr(data-footnote-number)`.
 *
 * Continuation lines must be indented (4 spaces or a tab); lazy continuation
 * (`[^1]: first line` / `second line` flush left) is deliberately not
 * supported, so a definition can never swallow the block that follows it.
 * The unindented line simply stays its own paragraph, which round-trips
 * stably.
 *
 * What the syntax MEANS lives in `footnote-syntax.ts`, shared with the
 * server-side renderers (Hearth's share page and email).
 */
import {
  Node,
  mergeAttributes,
  InputRule,
  type MarkdownLexerConfiguration,
  type MarkdownToken,
} from "@tiptap/core";
import type { Node as PMNode } from "@tiptap/pm/model";
import { Plugin, PluginKey, TextSelection } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";

import {
  CONTINUATION_INDENT,
  FOOTNOTE_DEFINITION,
  FOOTNOTE_DEFINITION_START_RE,
  FOOTNOTE_REFERENCE,
  FOOTNOTE_REFERENCE_INPUT_RE,
  FOOTNOTE_REFERENCE_START,
  buildFootnoteIndex,
  matchFootnoteDefinition,
  matchFootnoteReference,
  nextUnusedFootnoteLabel,
  type FootnoteIndex,
  type FootnoteMention,
} from "./footnote-syntax";

export {
  FOOTNOTE_DEFINITION,
  FOOTNOTE_REFERENCE,
  isFootnoteLabel,
  type FootnoteIndex,
} from "./footnote-syntax";

/**
 * True when the character at `index` is escaped by an odd run of backslashes.
 * (Twin of the same check in `math.ts` — marked applies this rule to every
 * delimiter, and the input rules have to agree with it or typing a construct
 * and loading it from storage produce different documents.)
 */
function isEscaped(text: string, index: number): boolean {
  let backslashes = 0;
  for (let i = index - 1; i >= 0 && text[i] === "\\"; i--) backslashes++;
  return backslashes % 2 === 1;
}

/** `[^label]` at the start of `src`, in marked's token shape. */
export function tokenizeFootnoteReference(
  src: string,
): MarkdownToken | undefined {
  const match = matchFootnoteReference(src);
  if (!match) return undefined;
  return {
    type: FOOTNOTE_REFERENCE,
    raw: match.raw,
    label: match.label,
  };
}

/** `[^label]: …` at the start of `src`, its body lexed into editable blocks. */
export function tokenizeFootnoteDefinition(
  src: string,
  lexer: MarkdownLexerConfiguration,
): MarkdownToken | undefined {
  const match = matchFootnoteDefinition(src);
  if (!match) return undefined;
  const { label, raw, text } = match;
  return {
    type: FOOTNOTE_DEFINITION,
    raw,
    label,
    text,
    // marked's block lexer needs the trailing newline, or the note's final
    // paragraph is mis-tokenized.
    tokens: text.trim() ? lexer.blockTokens(`${text}\n`) : [],
  };
}

/**
 * Inline `[^label]` reference. An atom: its rendered content is the footnote
 * *number*, which is positional, so there's nothing here for the user to edit
 * — editing happens in the definition.
 */
export const FootnoteReference = Node.create({
  name: FOOTNOTE_REFERENCE,
  group: "inline",
  inline: true,
  atom: true,
  // A reference is a citation of a note, not text with an identity of its
  // own — dragging one out of its sentence is never the intent.
  draggable: false,

  addAttributes() {
    return {
      label: {
        default: "",
        parseHTML: (el) => el.getAttribute("data-footnote-label") ?? "",
        renderHTML: (attrs) => ({
          "data-footnote-label": attrs.label as string,
        }),
      },
    };
  },

  parseHTML() {
    return [{ tag: "sup[data-footnote-ref]" }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      "sup",
      mergeAttributes(HTMLAttributes, {
        "data-footnote-ref": "",
        role: "doc-noteref",
      }),
    ];
  },

  markdownTokenizer: {
    name: FOOTNOTE_REFERENCE,
    level: "inline",
    start: (src: string) => src.indexOf(FOOTNOTE_REFERENCE_START),
    tokenize: tokenizeFootnoteReference,
  },

  parseMarkdown: (token, helpers) =>
    helpers.createNode(FOOTNOTE_REFERENCE, {
      label: String(token.label ?? ""),
    }),

  renderMarkdown: (node) => `[^${String(node.attrs?.label ?? "")}]`,

  addInputRules() {
    return [
      new InputRule({
        find: FOOTNOTE_REFERENCE_INPUT_RE,
        handler: ({ range, match, chain }) => {
          const label = match[1];
          if (!label) return null;
          // `\[^1]` is an escape: the markdown parser leaves it as literal
          // text, and this suffix-anchored rule would otherwise convert the
          // `[^1]` part, so a typed document and the same document reloaded
          // from storage would disagree about what's a footnote.
          if (isEscaped(match.input ?? "", match.index ?? 0)) return null;
          chain()
            .deleteRange(range)
            .insertContent({ type: FOOTNOTE_REFERENCE, attrs: { label } })
            .run();
        },
      }),
    ];
  },

  addCommands() {
    return {
      insertFootnote:
        () =>
        ({ state, tr, dispatch }) => {
          const referenceType = state.schema.nodes[FOOTNOTE_REFERENCE];
          const definitionType = state.schema.nodes[FOOTNOTE_DEFINITION];
          const paragraphType = state.schema.nodes.paragraph;
          if (!referenceType || !definitionType || !paragraphType) return false;
          // A footnote marks a *point* in the prose, so the reference goes at
          // the end of the selection and the selected text is left alone —
          // selecting a phrase and adding a note about it must never delete
          // the phrase. That makes `$to` the insertion point, so it's `$to`
          // that has to accept an inline node: code blocks are
          // `inlineContent` but hold no inline nodes, and a reference dropped
          // into one would serialize into the source.
          const { $to } = state.selection;
          if (!$to.parent.inlineContent || $to.parent.type.spec.code) {
            return false;
          }
          if (!dispatch) return true;

          const label = nextFootnoteLabel(state.doc);
          tr.insert($to.pos, referenceType.create({ label }));
          // Definitions live at the end of the document, which is both where
          // authors put them and where appending keeps existing ones grouped.
          const definitionPos = tr.doc.content.size;
          tr.insert(
            definitionPos,
            definitionType.create({ label }, paragraphType.create()),
          );
          // Land the cursor inside the empty note so typing continues there.
          tr.setSelection(
            TextSelection.near(tr.doc.resolve(definitionPos + 2)),
          );
          tr.scrollIntoView();
          return true;
        },
    };
  },

  addProseMirrorPlugins() {
    return [footnoteDecorationsPlugin()];
  },
});

/**
 * Block `[^label]: …` definition. `block+` content (rather than a single
 * paragraph) so a note can hold whatever the markdown held; `defining` keeps
 * it intact when content is pasted over it, the way blockquote does.
 */
export const FootnoteDefinition = Node.create({
  name: FOOTNOTE_DEFINITION,
  group: "block",
  content: "block+",
  defining: true,

  addAttributes() {
    return {
      label: {
        default: "",
        parseHTML: (el) => el.getAttribute("data-footnote-label") ?? "",
        renderHTML: (attrs) => ({
          "data-footnote-label": attrs.label as string,
        }),
      },
    };
  },

  parseHTML() {
    return [{ tag: "div[data-footnote-definition]" }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      "div",
      mergeAttributes(HTMLAttributes, {
        "data-footnote-definition": "",
        role: "doc-footnote",
      }),
      0,
    ];
  },

  markdownTokenizer: {
    name: FOOTNOTE_DEFINITION,
    level: "block",
    start: (src: string) => src.search(FOOTNOTE_DEFINITION_START_RE),
    tokenize: (src, _tokens, lexer) => tokenizeFootnoteDefinition(src, lexer),
  },

  parseMarkdown: (token, helpers) => {
    const parseBlockChildren =
      helpers.parseBlockChildren ?? helpers.parseChildren;
    const children = parseBlockChildren(token.tokens ?? []);
    return helpers.createNode(
      FOOTNOTE_DEFINITION,
      { label: String(token.label ?? "") },
      // `block+` needs at least one child, so an empty `[^1]:` gets an empty
      // paragraph to hold the cursor.
      children.length > 0 ? children : [{ type: "paragraph" }],
    );
  },

  renderMarkdown: (node, helpers) => {
    const label = String(node.attrs?.label ?? "");
    const children = node.content ?? [];
    const body = children
      .map(
        (child, index) =>
          helpers.renderChild?.(child, index) ??
          helpers.renderChildren([child]),
      )
      .join("\n\n");
    const [first = "", ...rest] = body.split("\n");
    // Every line after the first is indented so it reads as part of the note
    // rather than as the next block.
    const continuation = rest.map((line) =>
      line.trim() === "" ? "" : CONTINUATION_INDENT + line,
    );
    const head = first ? `[^${label}]: ${first}` : `[^${label}]:`;
    return continuation.length > 0
      ? `${head}\n${continuation.join("\n")}`
      : head;
  },
});

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    footnote: {
      /**
       * Insert a footnote: a reference at the cursor plus an empty
       * definition appended to the end of the document, with the cursor
       * moved into the definition. The label is the smallest unused
       * positive integer.
       */
      insertFootnote: () => ReturnType;
    };
  }
}

/** Number the document's footnotes — the shared rule over its own nodes. */
export function indexFootnotes(doc: PMNode): FootnoteIndex {
  const mentions: FootnoteMention[] = [];
  doc.descendants((node) => {
    const label = String(node.attrs?.label ?? "");
    if (!label) return true;
    if (node.type.name === FOOTNOTE_REFERENCE) {
      mentions.push({ kind: "reference", label });
    } else if (node.type.name === FOOTNOTE_DEFINITION) {
      mentions.push({ kind: "definition", label });
    }
    return true;
  });
  return buildFootnoteIndex(mentions);
}

/** The smallest positive integer not already used as a footnote label. */
export function nextFootnoteLabel(doc: PMNode): string {
  return nextUnusedFootnoteLabel(indexFootnotes(doc));
}

/** Document position of the definition for `label`, or null if there is none. */
function findFootnoteDefinition(doc: PMNode, label: string): number | null {
  let found: number | null = null;
  doc.descendants((node, pos) => {
    if (found !== null) return false;
    if (node.type.name === FOOTNOTE_DEFINITION && node.attrs.label === label) {
      found = pos;
      return false;
    }
    return true;
  });
  return found;
}

/**
 * Paint each reference and definition with its number, plus a flag for the
 * two broken states: a reference whose note is missing, and a note nothing
 * refers to. Node decorations merge these attributes onto the rendered
 * element, so the consumer's CSS can show the number without the schema
 * having to store a value that isn't in the markdown.
 */
function buildFootnoteDecorations(doc: PMNode): DecorationSet {
  const index = indexFootnotes(doc);
  const decorations: Decoration[] = [];
  doc.descendants((node, pos) => {
    const name = node.type.name;
    if (name !== FOOTNOTE_REFERENCE && name !== FOOTNOTE_DEFINITION)
      return true;
    const label = String(node.attrs.label ?? "");
    const attrs: Record<string, string> = {
      "data-footnote-number": String(index.numbers.get(label) ?? ""),
    };
    if (name === FOOTNOTE_REFERENCE && !index.defined.has(label)) {
      attrs["data-footnote-missing"] = "true";
    }
    if (name === FOOTNOTE_DEFINITION && !index.references.has(label)) {
      attrs["data-footnote-orphan"] = "true";
    }
    decorations.push(Decoration.node(pos, pos + node.nodeSize, attrs));
    return true;
  });
  return DecorationSet.create(doc, decorations);
}

/**
 * Numbering decorations + click-to-jump. Clicking a reference moves the
 * selection to its definition and scrolls it into view, which is the only
 * way to read a note whose text lives at the far end of a long document.
 */
function footnoteDecorationsPlugin(): Plugin<DecorationSet> {
  const key = new PluginKey<DecorationSet>("footnoteDecorations");
  return new Plugin<DecorationSet>({
    key,
    state: {
      init: (_, state) => buildFootnoteDecorations(state.doc),
      apply: (tr, previous) =>
        tr.docChanged
          ? buildFootnoteDecorations(tr.doc)
          : previous.map(tr.mapping, tr.doc),
    },
    props: {
      decorations(state) {
        return key.getState(state) ?? null;
      },
      handleClick(view, _pos, event) {
        if (event.button !== 0) return false;
        const eventTarget = event.target;
        if (!(eventTarget instanceof Element)) return false;
        const marker = eventTarget.closest("[data-footnote-ref]");
        if (!marker) return false;
        const label = marker.getAttribute("data-footnote-label");
        if (!label) return false;
        const definitionPos = findFootnoteDefinition(view.state.doc, label);
        if (definitionPos === null) return false;
        event.preventDefault();
        const selection = TextSelection.near(
          view.state.doc.resolve(definitionPos + 1),
        );
        view.dispatch(view.state.tr.setSelection(selection).scrollIntoView());
        if (view.editable) view.focus();
        // Returning true stops ProseMirror from also placing the cursor back
        // at the reference we just navigated away from.
        return true;
      },
    },
  });
}
